/**
 * Public Commodities Page ("Data Product" v3 - F design system).
 * Full-featured public view with global futures analysis + Nigerian market
 * commodities. Sections: Hero, 01 Global Futures, 02 Price Analysis,
 * 03 Nigerian Markets, CTA. Re-skinned to match Home.jsx: hairline cards,
 * mono figures (numericSx), DeltaChips, navy tooltips, SectionHeading intros.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  ButtonGroup,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Link as RouterLink } from 'react-router-dom';
import PublicHero from '../../components/public/PublicHero';
import SectionHeading from '../../components/public/SectionHeading';
import DeltaChip from '../../components/public/DeltaChip';
import { iconForCommodity } from '../../utils/commodityIcons';
import {
  useCommodities,
  useCommodityCoverage,
  useCategories,
  useGlobalCommodities,
  useGlobalPrices,
  useFxSeries,
  useGlobalSpecies,
  useSpeciesPrices,
  useLocalCommoditySeries,
  useWorldReferenceBatch,
  useWorldSeriesCatalogue,
} from '../../services/queries';
import { activeCommodities, activeCategories } from '../../utils/catalogStatus';
import { LoadingSkeleton, EmptyState, Sparkline, RangeLockWrap, WorldReferencePanel, WorldAttribution, ChartTooltip } from '../../components/common';
import { fadeUp, MotionInView } from '../../utils/animations';
import {
  WORLD_MONTHS, WORLD_COPY, WORLD_UNITS, DEFAULT_WORLD_SERIES,
  WORLD_CARD_SERIES, WORLD_TIME_RANGES, DEFAULT_WORLD_RANGE,
  toPriceMap, toSeriesMap, toPickerOptions,
  exchangeFeedIsEmpty, monthLabel, unitLabelFor, kgPerPublishedUnit, cardLabel,
  attributionOf,
} from '../../utils/worldReference';
import { useThemeMode, numericSx, radius, MONO } from '../../context/ThemeContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ArrowRight = ({ size = 15, color }) => <Icon icon="ph:arrow-right" width={size} height={size} color={color} />;

// Muted F ramp for compare overlays (dashed lines + chip dots).
const OVERLAY_RAMP = ['#0F2744', '#B45309', '#5B6472', '#0E7490', '#374151'];

// Native unit → kilograms conversion per commodity. Sourced from CME/ICE contract specs.
const KG_PER_NATIVE_UNIT = {
  GC001: 1000,     // Cocoa - USD/MT
  GC002: 27.2155,  // Soybeans - USD/Bushel (60 lb standard)
  GC003: 907.185,  // Soymeal - USD/short ton (CBOT)
  GC004: 0.453592, // Coffee - USD/lb
  GC005: 0.453592, // Sugar - USD/lb
};

// Local series are already in NGN, so their FX factor is a constant 1. Kept at
// module scope so it is referentially stable across renders.
const LOCAL_FX = () => 1;

const EMPTY_STATS = { current: null, previous: null, change: null, changePercent: null, high: null, low: null, asOf: null };

const convertPrice = (price, commodityId, targetUnit, kgPerNativeOverride) => {
  if (targetUnit === 'native') return price;
  // Species ids look like 'GC001-A' - fall back to the parent's kg factor so
  // sibling variants inherit the same per-unit conversion as their parent.
  const parentId = commodityId?.split('-')[0];
  // The override is for data whose unit is not an exchange contract, where
  // the caller knows the weight and this table does not.
  const kgPerNative = kgPerNativeOverride
    || KG_PER_NATIVE_UNIT[commodityId] || KG_PER_NATIVE_UNIT[parentId];
  if (!kgPerNative) return price;
  const pricePerKg = price / kgPerNative;
  if (targetUnit === 'kg') return pricePerKg;
  if (targetUnit === 'MT') return pricePerKg * 1000;
  if (targetUnit === 'lb') return pricePerKg * 0.453592;
  return price;
};

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'NGN', label: 'NGN (₦)', symbol: '₦' },
];

// Range values are DAY spans and series are cut by date, not point count:
// the global feed posts daily rows and local uploads are weekly, so a
// count-based slice would mean a different period per feed.
const TIME_RANGES = [
  { label: '1M', value: 31 },
  { label: '3M', value: 92 },
  { label: '6M', value: 183 },
  { label: '1Y', value: 366 },
  { label: 'ALL', value: 99999 },
];

// Format YYYY-MM-DD -> "18 Apr 2026"
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Sparkline for global commodity summary cards now lives in
// components/common/Sparkline (shared with the internal app).

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function PublicCommoditiesInner({ commodities, commodityCategories, globalCommodities, globalCommodityPrices: exchangePrices, coverage, fxRates, globalCommoditySpecies, globalSpeciesPrices, localCommodityPrices }) {
  // The ICE and CBOT feed was a paid vendor trial that expired, so in
  // production that map is empty and this whole section renders blank. When
  // it is empty we serve the same surfaces from the World Bank Pink Sheet and
  // relabel everything that claimed an exchange source. When it has rows,
  // nothing changes: exchange data wins.
  const usingWorldReference = exchangeFeedIsEmpty(exchangePrices);
  // The catalogue says which series exist; prices are then fetched BY SERIES
  // ID, so the picker can offer all 22 rather than only the five with a card.
  const { data: worldCatalogue } = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = useMemo(() => toPickerOptions(worldCatalogue), [worldCatalogue]);
  const allSeriesIds = useMemo(() => worldOptions.map((o) => o.id), [worldOptions]);

  const { data: worldBatch } = useWorldReferenceBatch(
    allSeriesIds, WORLD_MONTHS, { enabled: usingWorldReference && allSeriesIds.length > 0 },
  );
  // Keyed by series id: in world mode that is the only id space, so a card
  // and the picker select the same kind of thing.
  const worldPrices = useMemo(() => toPriceMap(worldBatch), [worldBatch]);
  const worldSeries = useMemo(() => toSeriesMap(worldBatch), [worldBatch]);
  const worldNotes = useMemo(() => {
    const out = {};
    const entries = worldBatch?.commodities || {};
    Object.keys(entries).forEach((id) => { out[id] = entries[id]?.discontinued || null; });
    return out;
  }, [worldBatch]);
  const worldAttribution = attributionOf(worldBatch);
  const globalCommodityPrices = usingWorldReference ? worldPrices : exchangePrices;

  // Tonne by default: the Pink Sheet quotes cocoa per kilo, so $5.95 is the
  // same price a futures screen calls $5,950, and the small number reads as a
  // collapse when it is not one.
  const [worldUnit, setWorldUnit] = useState('MT');

  // What the carousel shows. In world mode these are the crops Farmgate's
  // surveyors actually price, not the five exchange contracts we inherited,
  // four of which nobody here trades.
  const worldCards = useMemo(() => {
    const byId = Object.fromEntries(worldOptions.map((o) => [o.id, o]));
    return WORLD_CARD_SERIES.filter((id) => byId[id]).map((id) => {
      const { name, detail } = cardLabel(byId[id].label, byId[id].group);
      return { id, name, detail, fullName: byId[id].label, group: byId[id].group };
    });
  }, [worldOptions]);
  const { palette } = useThemeMode();

  const [selectedCommodity, setSelectedCommodity] = useState('GC001');
  const [selectedLocalCategory, setSelectedLocalCategory] = useState(
    () => commodities[0]?.category || 'CAT001'
  );
  const [overlays, setOverlays] = useState([]);
  const [timeRange, setTimeRange] = useState(183);
  // §03 filters - MULTI-select categories (empty = all) + free-text search.
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [commoditySearch, setCommoditySearch] = useState('');
  // Only relevant when comparing (overlays active). Default 'native' preserves the
  // familiar per-commodity unit when looking at a single line.
  const [normalizeUnit, setNormalizeUnit] = useState('native');
  const isComparing = overlays.length > 0;
  // Market mode (global futures vs local Nigerian market) and Compare mode
  // (across commodities vs species/variants of the focused commodity).
  const [marketMode, setMarketMode] = useState('global');
  const [compareMode, setCompareMode] = useState('commodities');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const isLocal = marketMode === 'local';
  // True only while WORLD data is on screen for a GLOBAL commodity. Local
  // commodities keep their own labelling either way.
  const worldView = usingWorldReference && !isLocal;
  // World figures always carry the chosen weight unit, not only while
  // comparing: the point of the tonne default is a familiar magnitude.
  const effectiveUnit = worldView ? worldUnit : (isComparing ? normalizeUnit : 'native');
  const findItem = (id) => {
    // A world series is a selection in its own right here, so it has to
    // answer like a catalogue row.
    const o = usingWorldReference ? worldOptions.find((x) => x.id === id) : null;
    if (o) return { id: o.id, name: o.label, unit: o.unit, category: o.group };
    return (
      globalCommodities.find((c) => c.id === id) ||
      Object.values(globalCommoditySpecies).flat().find((s) => s.id === id) ||
      commodities.find((c) => c.id === id)
    );
  };
  const priceSeriesForId = (id) =>
    globalCommodityPrices[id] ||
    globalSpeciesPrices[id] ||
    localCommodityPrices[id] ||
    [];
  // Stable overlay colour: by position in the active overlays list, so the
  // chart line and its compare chip always share the same ramp colour.
  const overlayColorFor = (overlayId) => {
    const idx = overlays.indexOf(overlayId);
    return OVERLAY_RAMP[(idx >= 0 ? idx : 0) % OVERLAY_RAMP.length];
  };
  const handleMarketChange = (_, newMode) => {
    if (!newMode || newMode === marketMode) return;
    setMarketMode(newMode);
    setOverlays([]);
    if (newMode === 'global') {
      // In world mode the ids are series, so GC001 would select nothing.
      setSelectedCommodity(usingWorldReference ? DEFAULT_WORLD_SERIES : 'GC001');
    } else {
      const firstLocal = commodities[0];
      setSelectedCommodity(firstLocal?.id || 'COM001');
      setSelectedLocalCategory(firstLocal?.category || 'CAT001');
    }
  };
  const handleLocalCategoryChange = (e) => {
    const newCat = e.target.value;
    setSelectedLocalCategory(newCat);
    const firstVariant = commodities.find((c) => c.category === newCat);
    if (firstVariant) {
      setSelectedCommodity(firstVariant.id);
      setOverlays([]);
    }
  };
  const localVariantsInCategory = useMemo(
    () => activeCommodities(commodities, commodityCategories).filter((c) => c.category === selectedLocalCategory),
    [selectedLocalCategory, commodities, commodityCategories]
  );
  const localCategoriesWithVariants = useMemo(() => {
    const ids = new Set(activeCommodities(commodities, commodityCategories).map((c) => c.category));
    return activeCategories(commodityCategories).filter((cat) => ids.has(cat.id));
  }, [commodities, commodityCategories]);
  // If the selected category is retired (or has no active variants), snap to
  // the first offered category so the variants browser never strands empty.
  useEffect(() => {
    if (localCategoriesWithVariants.length > 0 && !localCategoriesWithVariants.some((c) => c.id === selectedLocalCategory)) {
      setSelectedLocalCategory(localCategoriesWithVariants[0].id);
    }
  }, [localCategoriesWithVariants, selectedLocalCategory]);
  const handleCompareModeChange = (_, newMode) => {
    if (!newMode || newMode === compareMode) return;
    setCompareMode(newMode);
    setOverlays([]);
  };

  // Feed availability - the global futures + FX ingestion isn't connected yet, so gate
  // every "live" affordance on real data. These auto-recover once a feed posts prices.
  const globalPending = Object.keys(globalCommodityPrices).length === 0;
  // In world mode the unit comes from the series, not the exchange contract:
  // the Pink Sheet quotes $/mt and $/kg, never bushels or pounds.
  const unitFor = (id) => {
    const cat = globalCommodities.find((g) => g.id === id);
    return usingWorldReference
      ? unitLabelFor(worldSeries[id]?.unit, currency, worldUnit)
      : (currency === 'NGN' && !fxPending ? cat?.unit?.replace(/^USD/, 'NGN') : cat?.unit);
  };
  const fxPending = fxRates.length === 0;

  // Currency toggle for the chart + stats panel. NGN needs the FX feed, so it's locked
  // out (and never fabricates a rate) while fxPending.
  const [currency, setCurrency] = useState('USD');
  // Rebuild when the FX series arrives: with a frozen [] dep this map stayed
  // empty forever (rates load after first render), so fxFor silently fell back
  // to the latest rate for every historical point and flattened the naira
  // history into a pure dollar curve.
  const fxByDate = useMemo(() => {
    const m = {};
    fxRates.forEach((f) => { m[f.date] = f.rate; });
    return m;
  }, [fxRates]);
  const latestFxRate = fxRates[fxRates.length - 1]?.rate || 1550;
  const fxFor = useCallback(
    (date) => (currency === 'NGN' && !fxPending ? (fxByDate[date] ?? latestFxRate) : 1),
    [currency, fxPending, fxByDate, latestFxRate]
  );
  const currencySymbol = currency === 'NGN' && !fxPending ? '₦' : '$';

  // Carousel scroll controls for the global-commodity card row
  const cardScrollRef = useRef(null);
  // The section heading promises "click a card to load its chart below", and
  // the chart is a section away. Honour the promise rather than leaving the
  // reader to wonder whether the click did anything.
  const chartRef = useRef(null);
  const revealChart = () => {
    if (chartRef.current) chartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollCards = (dir) => {
    if (!cardScrollRef.current) return;
    cardScrollRef.current.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  // Keyboard activation for our clickable Box "buttons" (cards, filter chips).
  const onKeyActivate = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
  };

  // ---------- Active commodity data ----------
  // currentGC resolves through any of the three sources (global / species / local).
  // Local series are stored as NGN already, so we skip FX + per-kg normalize there.
  const currentGC = findItem(selectedCommodity);
  const fxForActive = useMemo(() => (isLocal ? LOCAL_FX : fxFor), [isLocal, fxFor]);
  const convertPriceActive = useCallback(
    (val, id) => {
      if (isLocal) return val;
      // A world series is published per kilo or per tonne, which the table of
      // bushel and pound weights knows nothing about. Without the factor it
      // would return the figure unconverted under a converted label.
      if (worldView) return convertPrice(val, id, effectiveUnit, kgPerPublishedUnit(worldSeries[id]?.unit));
      return convertPrice(val, id, effectiveUnit);
    },
    [isLocal, effectiveUnit, worldView, worldSeries]
  );
  const currencySymbolActive = isLocal ? '₦' : currencySymbol;

  const priceData = useMemo(() => {
    const data = priceSeriesForId(selectedCommodity);
    if (timeRange >= 99999) return data;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timeRange);
    const iso = cutoff.toISOString().slice(0, 10);
    return data.filter((d) => d.date >= iso);
    // globalCommodityPrices belongs here: it arrives asynchronously, and when
    // it is the world reference map it arrives a beat after the first render.
    // Without it the memo kept its first, empty answer and the chart, the
    // stats and the table all stayed blank while the cards above them showed
    // prices.
    // eslint-disable-next-line
  }, [selectedCommodity, timeRange, marketMode, globalCommodityPrices]);
  const hasSeries = priceData.length > 0;

  const chartData = useMemo(() => {
    // Overlays are joined BY DATE, not by array index: series can have
    // different lengths (e.g. one exchange closed on a day), and an index
    // join would plot different dates on the same x position.
    const overlayByDate = overlays.map((overlayId) => [
      overlayId,
      new Map((priceSeriesForId(overlayId) || []).map((p) => [p.date, p.close])),
    ]);
    return priceData.map((item) => {
      const fx = fxForActive(item.date);
      const dataPoint = { date: item.date };
      // Undefined, not zero, when there is no close. `null * fx` is 0, which
      // recharts plots as a real point, dragging the line to the axis floor
      // and the Y domain with it. Leaving the key out makes it a gap. The
      // overlay branch just below already got this right.
      if (item.close != null) {
        dataPoint[selectedCommodity] = convertPriceActive(item.close, selectedCommodity) * fx;
      }
      overlayByDate.forEach(([overlayId, byDate]) => {
        const close = byDate.get(item.date);
        if (close != null) {
          dataPoint[overlayId] = convertPriceActive(close, overlayId) * fx;
        }
      });
      return dataPoint;
    });
    // eslint-disable-next-line
  }, [priceData, selectedCommodity, overlays, timeRange, effectiveUnit, currency, marketMode, fxForActive, convertPriceActive]);

  // Axis date format adapts to the visible span: day + month inside half a
  // year, month + year beyond it (so ALL shows year boundaries clearly).
  const chartSpanDays = useMemo(() => {
    if (chartData.length < 2) return 0;
    return (new Date(chartData[chartData.length - 1].date) - new Date(chartData[0].date)) / 86400000;
  }, [chartData]);
  const fmtAxisDate = (d) => {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    if (chartSpanDays > 180) {
      const m = dt.toLocaleDateString('en-GB', { month: 'short' });
      return `${m} '${String(dt.getFullYear()).slice(2)}`;
    }
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Null means "no figure", and it stays null the whole way to the screen.
  // open/high/low/close are all nullable and the API passes nulls through, so
  // multiplying by the exchange rate first turned every one of them into a
  // confident 0: `null * fx` is 0, `Math.max(null)` is 0, and a row of zeros
  // reads as a market that traded at nothing rather than one we have no
  // figures for. Same door as the one closed on the premium page.
  const stats = useMemo(() => {
    if (priceData.length === 0) return EMPTY_STATS;
    const conv = (v, fx) => (v == null ? null : convertPriceActive(v, selectedCommodity) * fx);
    const converted = priceData.map((item) => {
      const fx = fxForActive(item.date);
      return { date: item.date, close: conv(item.close, fx), high: conv(item.high, fx), low: conv(item.low, fx) };
    });
    const priced = converted.filter((d) => d.close != null);
    const closes = priced.map((d) => d.close);
    const highs = converted.map((d) => d.high).filter((v) => v != null);
    const lows = converted.map((d) => d.low).filter((v) => v != null);
    if (closes.length === 0 && highs.length === 0 && lows.length === 0) return EMPTY_STATS;

    const current = closes.length ? closes[closes.length - 1] : null;
    const previous = closes.length > 1 ? closes[closes.length - 2] : null;
    // A previous price of zero yields no percentage, so it is not treated as
    // a comparison point at all rather than being reported as no change.
    const comparable = current != null && previous != null && previous !== 0;
    const change = comparable ? current - previous : null;
    const changePercent = comparable ? (change / previous) * 100 : null;
    // A monthly average has no intraday high or low, so when those fields are
    // absent the highest and lowest MONTH is the right meaning, and the tiles
    // are relabelled to say so. Falling back to the closes keeps them useful
    // without inventing an OHLC range the source never published.
    const highSource = highs.length ? highs : closes;
    const lowSource = lows.length ? lows : closes;
    const high = highSource.length ? Math.max(...highSource) : null;
    const low = lowSource.length ? Math.min(...lowSource) : null;
    // The date of the newest actual figure, not of the newest row. A trailing
    // row carrying no price must not let the header claim the series is
    // fresher than the number beside it.
    const asOf = priced.length ? priced[priced.length - 1].date : null;
    return { current, previous, change, changePercent, high, low, asOf };
  }, [priceData, selectedCommodity, fxForActive, convertPriceActive]);

  // ---------- Local commodity data (§03) ----------
  const categories = useMemo(() => activeCategories(commodityCategories), [commodityCategories]);

  const toggleCategory = (id) =>
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );

  const filteredLocalCommodities = useMemo(() => {
    const q = commoditySearch.trim().toLowerCase();
    // Showcase surface: a retired commodity (or one whose category was
    // retired) drops out of the browse table entirely. Recorded price
    // history keeps resolving its name elsewhere, per catalogStatus.
    return activeCommodities(commodities, commodityCategories).filter((c) => {
      const catOk = selectedCategories.length === 0 || selectedCategories.includes(c.category);
      if (!catOk) return false;
      if (!q) return true;
      const name = (c.name || '').toLowerCase();
      const canon = (c.canonicalName || '').toLowerCase();
      return name.includes(q) || canon.includes(q);
    });
  }, [selectedCategories, commoditySearch, commodities, commodityCategories]);

  // §03 pagination - the "All" view is long, so page the table (like Price Records).
  const [commPage, setCommPage] = useState(0);
  const [commRowsPerPage, setCommRowsPerPage] = useState(12);
  // Clamp the rendered page so a shrinking result set never drives the table out of
  // range (avoids MUI's out-of-range warning in the render before the reset effect).
  const commPageCount = Math.max(1, Math.ceil(filteredLocalCommodities.length / commRowsPerPage));
  const commSafePage = Math.min(commPage, commPageCount - 1);
  const pagedCommodities = useMemo(
    () => filteredLocalCommodities.slice(commSafePage * commRowsPerPage, commSafePage * commRowsPerPage + commRowsPerPage),
    [filteredLocalCommodities, commSafePage, commRowsPerPage]
  );
  // Any filter change resets to the first page.
  // World ids are series ids. A selection left over from the exchange id
  // space resolves to nothing, so snap it to something real.
  // A day-count range from the exchange buttons is not one of the year
  // buttons world mode offers, so nothing would look selected. Snap it once.
  const worldRangeSetRef = useRef(false);
  useEffect(() => {
    if (!worldView) { worldRangeSetRef.current = false; return; }
    if (worldRangeSetRef.current) return;
    worldRangeSetRef.current = true;
    if (!WORLD_TIME_RANGES.some((r) => r.value === timeRange)) setTimeRange(DEFAULT_WORLD_RANGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldView]);

  useEffect(() => {
    if (!worldView || allSeriesIds.length === 0) return;
    if (!allSeriesIds.includes(selectedCommodity)) {
      setSelectedCommodity(
        allSeriesIds.includes(DEFAULT_WORLD_SERIES) ? DEFAULT_WORLD_SERIES : allSeriesIds[0],
      );
    }
  }, [worldView, allSeriesIds, selectedCommodity]);

  // Picking a series whose data stopped years ago cannot show anything in a
  // six month window, so the reader would get a warning and then four dashes.
  // Widen once, on the switch itself, so the history is actually on screen.
  const lastWorldSeriesRef = useRef(null);
  useEffect(() => {
    if (!worldView || lastWorldSeriesRef.current === selectedCommodity) return;
    lastWorldSeriesRef.current = selectedCommodity;
    const rows = worldPrices[selectedCommodity] || [];
    if (rows.length === 0) return;
    const newest = new Date(rows[rows.length - 1].date);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - timeRange);
    if (timeRange < 99999 && newest < cutoff) setTimeRange(99999);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // timeRange is read, not depended on: this fires when the series changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worldView, selectedCommodity, worldPrices]);

  useEffect(() => { setCommPage(0); }, [selectedCategories, commoditySearch]);

  // ---------- Format helpers ----------
  const formatPrice = (value) => {
    if (value == null) return '-';
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDollar = (value) => `${currencySymbolActive}${formatPrice(value)}`;

  // One figure in the statistics list, or a dash when there is no figure.
  // Written once so the four rows cannot drift apart on what "missing" looks
  // like, which is how one of them ended up printing a zero.
  const statFigure = (value) => (
    <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1rem', color: value != null ? palette.text : palette.textMuted }}>
      {value == null ? '-' : formatDollar(value)}
    </Typography>
  );

  // Unit label for the primary stat figure. Local commodities carry no `unit` field and
  // their series is a per-bag ₦ price, so label it explicitly.
  const unitLabel = isLocal
    ? 'per bag'
    : worldView
      ? unitFor(selectedCommodity)
      : effectiveUnit === 'native'
        ? (currency === 'NGN' && !fxPending ? currentGC?.unit?.replace(/^USD/, 'NGN') : currentGC?.unit)
        : `${currency}/${normalizeUnit}`;

  // ---------- Shared F recipes (Home patterns) ----------
  const cardSx = {
    bgcolor: palette.card,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: `${radius.card}px`,
    boxShadow: palette.shadowSm,
  };
  const tsSx = { fontSize: '0.62rem', color: palette.textMuted, fontStyle: 'italic', letterSpacing: '0.02em' };
  const statLabelSx = { fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: palette.textMuted, mb: 0.5 };

  const ghostIconSx = {
    width: 34, height: 34, borderRadius: `${radius.control}px`,
    border: `1px solid ${palette.cardBorder}`, bgcolor: palette.card, color: palette.textSecondary,
    '&:hover': { bgcolor: palette.surface, borderColor: palette.textMuted, color: palette.text },
  };

  // Category filter chip (multi-select).
  const catChipSx = (active) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', userSelect: 'none',
    fontWeight: 600, fontSize: '0.8rem', borderRadius: 999, px: 1.6, py: 0.7,
    bgcolor: active ? palette.accent : palette.card,
    color: active ? '#fff' : palette.textSecondary,
    border: `1px solid ${active ? palette.accent : palette.cardBorder}`,
    transition: 'background-color .2s, border-color .2s, color .2s',
    '&:hover': { borderColor: active ? palette.accent : palette.textMuted, bgcolor: active ? palette.accent : palette.surface },
  });

  // Range button (mono labels, green active).
  const rangeBtnSx = (active) => ({
    minWidth: 40, px: 1.1, py: 0.35,
    fontFamily: MONO, fontSize: '0.7rem', fontWeight: 600,
    color: active ? '#fff' : palette.textSecondary,
    bgcolor: active ? palette.accent : 'transparent',
    borderColor: palette.cardBorder,
    '&:hover': { bgcolor: active ? palette.accent : palette.surfaceHover, borderColor: active ? palette.accent : palette.textMuted },
  });

  // Toggle group (light accent wash on active - matches the Markets page toggles).
  const toggleGroupSx = {
    '& .MuiToggleButton-root': {
      textTransform: 'none', fontWeight: 600, color: palette.textSecondary, borderColor: palette.cardBorder,
      '&.Mui-selected': { bgcolor: palette.accentBg, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } },
    },
  };

  // Table pagination (matches the Markets Price Records footer).
  const paginationSx = {
    color: palette.textSecondary,
    '& .MuiTablePagination-toolbar': { minHeight: 44, pl: 0 },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { ...numericSx, fontSize: '0.78rem', color: palette.textSecondary },
    '& .MuiTablePagination-select': { ...numericSx, fontSize: '0.78rem' },
    '& .MuiSvgIcon-root': { color: palette.textMuted },
  };

  // ---------- Chart styles (Home patterns) ----------
  const gridStyle = { stroke: palette.chartGrid, strokeDasharray: '3 3', vertical: false };
  const axisStyle = {
    tick: { fontSize: 11, fill: palette.chartAxis, fontFamily: MONO },
    axisLine: false,
    tickLine: false,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.bg }}>
      {/* Hero (untouched) */}
      <PublicHero
        eyebrow="Market Intelligence"
        title="Global & Local Commodities"
        subtitle={
          usingWorldReference
            ? `World reference prices and ${commodities.length} commodity variants tracked across Nigerian markets.`
            : `Global futures benchmarks and ${commodities.length} commodity variants tracked across Nigerian markets.`
        }
      />

      {/* ================================================================ */}
      {/* 01 / GLOBAL FUTURES                                               */}
      {/* ================================================================ */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={usingWorldReference ? WORLD_COPY.eyebrow : 'Global Futures'}
            number="01"
            title={usingWorldReference ? WORLD_COPY.sectionTitle : 'Exchange-Traded Commodities'}
            subtitle={
              usingWorldReference
                ? `${WORLD_COPY.subtitle} Click a card to load its chart below.`
                : (globalPending
                    ? 'Benchmarks from ICE and CBOT - live prices connecting soon. Click a card to load its chart below.'
                    : 'Benchmarks from ICE and CBOT, updated daily after settlement. Click a card to load its chart below.')}
            actions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Unit sits beside Currency because it has the same reach:
                    both restate every figure on the page, cards included.
                    Down by the chart it sat next to the commodity picker,
                    which IS chart-only, and read as if it were too. */}
                {worldView && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select value={worldUnit} label="Unit" onChange={(e) => setWorldUnit(e.target.value)}>
                      {WORLD_UNITS.map((u) => (
                        <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Currency</InputLabel>
                  <Select value={currency} label="Currency" onChange={(e) => setCurrency(e.target.value)}>
                    {CURRENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} disabled={opt.value === 'NGN' && fxPending}>
                        {opt.label}{opt.value === 'NGN' && fxPending ? ' - needs FX feed' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Only while the cards actually scroll. From lg they all
                    fit, and arrows that move nothing are clutter. */}
                <IconButton onClick={() => scrollCards(-1)} size="small" sx={{ ...ghostIconSx, display: { xs: 'inline-flex', lg: 'none' } }} aria-label="Scroll commodities left">
                  <Icon icon="ph:caret-left" width={16} height={16} />
                </IconButton>
                <IconButton onClick={() => scrollCards(1)} size="small" sx={{ ...ghostIconSx, display: { xs: 'inline-flex', lg: 'none' } }} aria-label="Scroll commodities right">
                  <Icon icon="ph:caret-right" width={16} height={16} />
                </IconButton>
              </Box>
            }
            sx={{ mb: { xs: 3, md: 4 } }}
          />

          {globalPending && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, px: 1.75, py: 1, borderRadius: `${radius.control}px`, bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}` }}>
              <Icon icon="ph:plugs" width={16} height={16} color={palette.textMuted} />
              <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary }}>
                {usingWorldReference
                  ? 'World reference prices are loading - showing benchmark definitions for now.'
                  : 'Live exchange prices are being connected - showing benchmark definitions for now.'}
              </Typography>
            </Box>
          )}

          <Box
            ref={cardScrollRef}
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 1,
              mx: -1,
              px: 1,
              scrollSnapType: 'x mandatory',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {(worldView ? worldCards : globalCommodities).map((gc) => {
              // Only rows that actually carry a close. A trailing row with no
              // figure is ordinary, and multiplying its null by the exchange
              // rate produced a confident 0 that the card printed as a price.
              // In world mode the card IS a series, so its id needs no
              // translation: one id space for cards, picker and chart.
              const cardId = gc.id;
              const prices = (globalCommodityPrices[cardId] || []).filter((r) => r.close != null);
              const latestRow = prices[prices.length - 1];
              const prevRow = prices.length > 1 ? prices[prices.length - 2] : latestRow;
              // Converted, not merely relabelled: the card prints cardUnit
              // beside these, and a $/kg figure under a $/mt label is off by
              // a thousand.
              const latestPriceConv = latestRow
                ? convertPriceActive(latestRow.close, cardId) * fxFor(latestRow.date)
                : null;
              const prevPriceConv = prevRow
                ? convertPriceActive(prevRow.close, cardId) * fxFor(prevRow.date)
                : latestPriceConv;
              // Null, not 0, with nothing to compare against. A single priced
              // row used to render a green "+0.00%", which claims the market
              // held steady when the truth is that we have one observation.
              const change = latestPriceConv != null && prevPriceConv
                ? ((latestPriceConv - prevPriceConv) / prevPriceConv) * 100
                : null;
              const isUp = change == null || change >= 0;
              const isSelected = cardId === selectedCommodity;
              const sparkData = prices.slice(-16);
              const cardUnit = unitFor(cardId);
              const cardPending = latestPriceConv == null;
              const selectCard = () => {
                // Carousel is global-only - auto-flip back to Global
                // when clicked while the chart is in Local mode.
                if (isLocal) {
                  setMarketMode('global');
                  setOverlays([]);
                  setSnackbar({ open: true, message: 'Switched to Global Market' });
                } else {
                  setOverlays((prev) => prev.filter((id) => id !== gc.id));
                }
                setSelectedCommodity(cardId);
                revealChart();
              };

              return (
                // Five fixed-width cards came to more than the container, so
                // the fifth always bled off the edge and read as broken. From
                // lg they share the row instead; below that they stay a
                // scroller, which is the right behaviour on a phone.
                <Box key={gc.id} sx={{ minWidth: { xs: 210, sm: 232, lg: 0 }, flex: { lg: '1 1 0' }, flexShrink: 0, scrollSnapAlign: 'start' }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`Load ${gc.fullName || gc.name} price chart`}
                    onClick={selectCard}
                    onKeyDown={onKeyActivate(selectCard)}
                    sx={{
                      ...cardSx,
                      cursor: 'pointer',
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: isSelected ? `1.5px solid ${palette.accent}` : `1px solid ${palette.cardBorder}`,
                      bgcolor: isSelected ? palette.accentBg : palette.card,
                      transition: 'border-color .2s, box-shadow .2s',
                      '&:hover': isSelected ? {} : { borderColor: palette.textMuted, boxShadow: palette.shadowMd },
                      '&:focus-visible': { outline: `2px solid ${palette.accent}`, outlineOffset: 2 },
                    }}
                  >
                    {/* Header - icon tile + name + symbol·exchange, delta or "soon" right */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                        {/* No icon. Every card in this row carried the same
                            globe, so it never distinguished one from another,
                            and the 40px it cost is the difference between
                            "Groundnuts" and "Groundn...". */}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.86rem', color: palette.text, lineHeight: 1.2 }}>{gc.name}</Typography>
                          {/* No contract symbol or exchange while world data
                              is showing: "CC · ICE" above a World Bank figure
                              is a false attribution. */}
                          <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.66rem', color: palette.textMuted }}>
                            {worldView
                              ? (gc.detail || 'World Bank')
                              : `${gc.symbol} · ${gc.exchange}`}
                          </Typography>
                        </Box>
                      </Box>
                      {/* The change belongs here, opposite the name. It only
                          crowded the name while that name was the full series
                          label; now it is the crop alone and both fit. */}
                      {cardPending ? (
                        <Box sx={{ px: 0.9, py: 0.3, borderRadius: 999, bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, flexShrink: 0 }}>
                          <Typography sx={{ fontFamily: MONO, fontSize: '0.58rem', fontWeight: 700, color: palette.textMuted, letterSpacing: '0.06em' }}>SOON</Typography>
                        </Box>
                      ) : change == null ? null : (
                        <Box sx={{ flexShrink: 0 }}><DeltaChip value={change} decimals={2} size="sm" /></Box>
                      )}
                    </Box>

                    {/* Price + unit */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                      <Typography noWrap sx={{ ...numericSx, fontWeight: 600, fontSize: '1.3rem', color: cardPending ? palette.textMuted : palette.text }}>
                        {cardPending ? '-' : `${currencySymbol}${formatPrice(latestPriceConv)}`}
                      </Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: palette.textMuted }}>{cardUnit}</Typography>
                    </Box>

                    {/* Sparkline, or a muted baseline while the feed is pending */}
                    <Box sx={{ mt: 1, height: 40, display: 'flex', alignItems: 'center' }}>
                      {cardPending ? (
                        <Box sx={{ width: '100%', borderTop: `1px dashed ${palette.cardBorder}` }} />
                      ) : (
                        <Sparkline data={sparkData} color={isUp ? palette.accent : palette.danger} id={gc.id} />
                      )}
                    </Box>

                    {/* Updated stamp */}
                    <Typography sx={{ ...tsSx, mt: 'auto', pt: 1 }}>
                      {cardPending
                        ? (usingWorldReference ? 'Awaiting world reference' : 'Awaiting exchange feed')
                        : (usingWorldReference
                            ? monthLabel(latestRow?.date)
                            : `Updated · ${fmtDate(latestRow?.date)}`)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {worldView && (
            <WorldAttribution text={worldAttribution} sx={{ mt: 1.5 }} />
          )}
        </Container>
      </Box>

      {/* ================================================================ */}
      {/* 02 / PRICE ANALYSIS                                               */}
      {/* ================================================================ */}
      <Box component="section" ref={chartRef} sx={{ py: { xs: 6, md: 8 }, bgcolor: palette.surface, borderTop: `1px solid ${palette.divider}`, borderBottom: `1px solid ${palette.divider}` }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Price Analysis"
            number="02"
            title="Interactive Price Charts"
            subtitle="Compare commodities and variants over time, normalized across units and currencies."
            sx={{ mb: { xs: 3, md: 4 } }}
          />

          <MotionInView variants={fadeUp}>
          <Grid container spacing={2.5} sx={{ alignItems: 'stretch' }}>
            {/* ---------- Chart Area (md=8) ---------- */}
            <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                <Box sx={{ ...cardSx, p: 2.5, width: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Row 1 - market mode + local selects (its own row) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 2, pb: 2, borderBottom: `1px solid ${palette.divider}` }}>
                    <ToggleButtonGroup size="small" exclusive value={marketMode} onChange={handleMarketChange} aria-label="Market" sx={toggleGroupSx}>
                      <ToggleButton value="global" sx={{ px: 2 }}>Global Market</ToggleButton>
                      <ToggleButton value="local" sx={{ px: 2 }}>Local Market</ToggleButton>
                    </ToggleButtonGroup>
                    {/* The chart picks its own commodity. The cards above are
                        an overview: reaching them means scrolling up and back
                        down, and they cover five of twenty two series. */}
                    {worldView && worldOptions.length > 0 && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Commodity</InputLabel>
                          <Select
                            value={allSeriesIds.includes(selectedCommodity) ? selectedCommodity : ''}
                            label="Commodity"
                            onChange={(e) => { setSelectedCommodity(e.target.value); setOverlays([]); }}
                          >
                            {worldOptions.flatMap((opt, i, arr) => {
                              const isNewGroup = i === 0 || arr[i - 1].group !== opt.group;
                              const rows = isNewGroup
                                ? [<ListSubheader key={`h-${opt.group}`}>{opt.group}</ListSubheader>]
                                : [];
                              rows.push(
                                <MenuItem key={opt.id} value={opt.id}>
                                  {opt.label}
                                  {worldNotes[opt.id] && (
                                    <Box component="span" sx={{ ml: 1, fontSize: '0.65rem', color: palette.textMuted }}>
                                      (ended)
                                    </Box>
                                  )}
                                </MenuItem>,
                              );
                              return rows;
                            })}
                          </Select>
                        </FormControl>
                      </>
                    )}
                    <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center' }}>
                      <ButtonGroup size="small">
                        {(worldView ? WORLD_TIME_RANGES : TIME_RANGES).map((range) => (
                          <RangeLockWrap key={range.label} days={range.value}>
                            <Button onClick={() => setTimeRange(range.value)} sx={rangeBtnSx(timeRange === range.value)}>
                              {range.label}
                            </Button>
                          </RangeLockWrap>
                        ))}
                      </ButtonGroup>
                    </Box>
                    {isLocal && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Local commodity</InputLabel>
                          <Select value={selectedLocalCategory} label="Local commodity" onChange={handleLocalCategoryChange}>
                            {localCategoriesWithVariants.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Species</InputLabel>
                          <Select value={selectedCommodity} label="Species" onChange={(e) => { setSelectedCommodity(e.target.value); setOverlays([]); }}>
                            {localVariantsInCategory.map((c) => (
                              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Box>

                  {/* Row 2 - title + chart-scoped controls (normalize, timeframe) */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: palette.text, lineHeight: 1.25, fontSize: { xs: '1.1rem', md: '1.3rem' }, letterSpacing: '-0.01em' }}>
                        {currentGC?.name || ''} {isLocal ? 'Price Trend' : 'Price Chart'}
                      </Typography>
                      <Typography sx={{ ...tsSx, mt: 0.4 }}>
                        {worldView
                          ? `${WORLD_COPY.sourceLine} · ${monthLabel(stats.asOf)}`
                          : `Updated · ${fmtDate(stats.asOf)}`}
                        {worldView && worldNotes[selectedCommodity] && (
                          <Box component="span" sx={{ display: 'block', mt: 0.5, color: palette.warning, fontStyle: 'normal' }}>
                            {worldNotes[selectedCommodity]}
                          </Box>
                        )}
                        {!isLocal && isComparing && normalizeUnit !== 'native' && (
                          <> · Normalized to {currency}/{normalizeUnit}</>
                        )}
                      </Typography>
                      {!isLocal && currency === 'NGN' && !fxPending && (
                        <Typography sx={{ ...tsSx, mt: 0.2 }}>Converted at NGN/USD ≈ {Math.round(latestFxRate)}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Time range moved up to the control bar, beside the
                          other things that change what the chart shows. It
                          used to sit here against the title, which split the
                          controls across two rows with a heading between. */}
                      {/* Not in world mode: the Unit control up beside
                          Currency already governs the weight there, so this
                          one changes nothing and only implies it does. */}
                      {!isLocal && !worldView && isComparing && (
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <InputLabel>Normalize</InputLabel>
                          <Select value={normalizeUnit} label="Normalize" onChange={(e) => setNormalizeUnit(e.target.value)}>
                            <MenuItem value="native">Native</MenuItem>
                            <MenuItem value="kg">{currency}/kg</MenuItem>
                            <MenuItem value="MT">{currency}/MT</MenuItem>
                            <MenuItem value="lb">{currency}/lb</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Box>

                  {/* Stat tiles - figures fall back to '-' until the active series has data */}
                  <Box sx={{ display: 'flex', gap: { xs: 2.5, md: 4 }, mb: 2.5, pb: 2.5, flexWrap: 'wrap', borderBottom: `1px solid ${palette.divider}` }}>
                    <Box>
                      <Typography sx={statLabelSx}>Current Price</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                        <Typography sx={{ ...numericSx, fontSize: '1.35rem', fontWeight: 600, color: stats.current != null ? palette.text : palette.textMuted }}>
                          {stats.current == null ? '-' : formatDollar(stats.current)}
                        </Typography>
                        {stats.current != null && <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: palette.textMuted }}>{unitLabel}</Typography>}
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={statLabelSx}>{worldView ? WORLD_COPY.changeLabel : 'Change (latest)'}</Typography>
                      {stats.change != null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ ...numericSx, fontSize: '1.35rem', fontWeight: 600, color: stats.change === 0 ? palette.textSecondary : (stats.change > 0 ? palette.accent : palette.danger) }}>
                            {stats.change > 0 ? '+' : (stats.change < 0 ? '−' : '')}{Math.abs(stats.change).toFixed(2)}
                          </Typography>
                          <DeltaChip value={stats.changePercent} decimals={2} size="sm" />
                        </Box>
                      ) : (
                        <Typography sx={{ ...numericSx, fontSize: '1.35rem', fontWeight: 600, color: palette.textMuted }}>-</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography sx={statLabelSx}>{worldView ? WORLD_COPY.highLabel : 'Period High'}</Typography>
                      <Typography sx={{ ...numericSx, fontSize: '1.2rem', fontWeight: 600, color: stats.high != null ? palette.text : palette.textMuted }}>{stats.high == null ? '-' : formatDollar(stats.high)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={statLabelSx}>{worldView ? WORLD_COPY.lowLabel : 'Period Low'}</Typography>
                      <Typography sx={{ ...numericSx, fontSize: '1.2rem', fontWeight: 600, color: stats.low != null ? palette.text : palette.textMuted }}>{stats.low == null ? '-' : formatDollar(stats.low)}</Typography>
                    </Box>
                  </Box>

                  {/* Chart */}
                  <Box sx={{ width: '100%', height: 400 }}>
                    {(priceData && priceData.length > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData}>
                        <CartesianGrid {...gridStyle} />
                        <XAxis dataKey="date" {...axisStyle} minTickGap={28} tickFormatter={fmtAxisDate} />
                        <YAxis
                          {...axisStyle}
                          domain={['auto', 'auto']}
                          tickFormatter={(v) =>
                            `${currencySymbolActive}${v >= 1000
                              ? `${(v / 1000).toFixed(1)}k`
                              : v.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                          }
                        />
                        {/* Rows sorted by value, so the tooltip order matches
                            the order the lines sit in on screen, and each row
                            carries its line's colour. */}
                        <RechartsTooltip
                          cursor={{ stroke: palette.divider }}
                          content={
                            <ChartTooltip
                              highlightKey={selectedCommodity}
                              labelFormatter={(d) => (worldView ? monthLabel(d) : fmtDate(d))}
                              nameFormatter={(name) => findItem(name)?.name || name}
                              valueFormatter={(v) => `${currencySymbolActive}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            />
                          }
                        />
                        <Legend wrapperStyle={{ color: palette.textSecondary, fontSize: '0.75rem' }} />

                        {/* Primary commodity - solid green line */}
                        <Line
                          type={worldView ? "stepAfter" : "monotone"}
                          dataKey={selectedCommodity}
                          stroke={palette.accent}
                          strokeWidth={2.5}
                          dot={false}
                          name={currentGC?.name}
                        />

                        {/* Overlay commodities - dashed muted-ramp lines */}
                        {overlays.map((overlayId) => {
                          const overlay = findItem(overlayId);
                          return (
                            <Line
                              key={overlayId}
                              type={worldView ? "stepAfter" : "monotone"}
                              dataKey={overlayId}
                              stroke={overlayColorFor(overlayId)}
                              strokeWidth={1.5}
                              strokeDasharray="5 5"
                              dot={false}
                              name={overlay?.name}
                            />
                          );
                        })}
                      </ComposedChart>
                    </ResponsiveContainer>
                    ) : (() => {
                      // Data exists but none inside the selected range: say so
                      // (instead of pretending the series has no history at all).
                      const full = priceSeriesForId(selectedCommodity);
                      if (full.length > 0) {
                        return (
                          <EmptyState
                            compact
                            icon={<Icon icon="ph:calendar-blank" width={24} height={24} />}
                            title="No prices in this range"
                            description={`The latest data for this commodity is ${fmtDate(full[full.length - 1].date)}. Choose a longer range like 1Y or ALL.`}
                            sx={{ height: 400 }}
                          />
                        );
                      }
                      return (
                        <EmptyState
                          compact
                          icon={<Icon icon={isLocal ? 'ph:chart-line' : 'ph:plugs'} width={24} height={24} />}
                          title={isLocal
                            ? 'No price history yet'
                            : (usingWorldReference ? WORLD_COPY.emptyTitle : 'Live futures prices are being connected')}
                          description={isLocal
                            ? 'Weekly prices for this commodity will appear here once uploads are in.'
                            : (usingWorldReference
                                ? WORLD_COPY.emptyBody
                                : 'Exchange settlement prices will populate this chart once the ICE / CBOT feed is connected.')}
                          sx={{ height: 400 }}
                        />
                      );
                    })()}
                  </Box>
                </Box>
                {isLocal && <WorldReferencePanel commodityId={selectedCommodity} />}
            </Grid>

            {/* ---------- Sidebar (md=4) ---------- */}
            <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Compare card */}
                  <Box sx={{ ...cardSx, p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text, mb: 1.5, letterSpacing: '-0.01em' }}>
                      {compareMode === 'species' && !worldView ? 'Compare Species' : 'Compare Commodities'}
                    </Typography>
                    {/* A world series has no variants, so the Species half
                        of this toggle would only ever offer an empty list. */}
                    {!worldView && (
                      <ToggleButtonGroup size="small" exclusive value={compareMode} onChange={handleCompareModeChange} aria-label="Compare mode" fullWidth sx={{ ...toggleGroupSx, mb: 2 }}>
                        <ToggleButton value="commodities" sx={{ flex: 1 }}>Commodities</ToggleButton>
                        <ToggleButton value="species" sx={{ flex: 1 }}>Species</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                    {(() => {
                      let options = [];
                        // In world mode the comparable things are the world
                        // series, all 22 of them, not the five exchange
                        // contracts. Offering the old catalogue here meant
                        // picking a commodity whose id the chart no longer
                        // knows, so the overlay drew nothing.
                      if (compareMode === 'commodities') {
                        options = (isLocal
                          ? commodities
                          : (worldView ? worldOptions.map((o) => ({ id: o.id, name: o.label })) : globalCommodities)
                        ).filter((c) => c.id !== selectedCommodity);
                      } else if (isLocal) {
                        const cat = currentGC?.category;
                        options = cat
                          ? commodities.filter((c) => c.id !== selectedCommodity && c.category === cat)
                          : [];
                      } else {
                        // Only offer variants that actually have a price series;
                        // the variant-level feed is not connected yet, so an
                        // unfiltered pick would plot an invisible empty line.
                        const variants = globalCommoditySpecies[selectedCommodity] || [];
                        options = variants.filter((s) => priceSeriesForId(s.id).length > 0);
                        if (variants.length > 0 && options.length === 0) {
                          return (
                            <Box sx={{ p: 2, textAlign: 'center', border: `1px dashed ${palette.cardBorder}`, borderRadius: `${radius.control}px` }}>
                              <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted }}>
                                {`${currentGC?.name || 'This commodity'} has ${variants.length} tracked variants, but variant-level price feeds are not connected yet. They will be comparable here once variant prices arrive.`}
                              </Typography>
                            </Box>
                          );
                        }
                      }
                      if (options.length === 0) {
                        return (
                          <Box sx={{ p: 2, textAlign: 'center', border: `1px dashed ${palette.cardBorder}`, borderRadius: `${radius.control}px` }}>
                            <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted }}>
                              No comparable {compareMode === 'species' ? 'variants' : 'commodities'} available.
                            </Typography>
                          </Box>
                        );
                      }
                      const selected = options.filter((o) => overlays.includes(o.id));
                      return (
                        <Autocomplete
                          multiple
                          size="small"
                          options={options}
                          value={selected}
                          onChange={(_, picks) => setOverlays(picks.map((p) => p.id))}
                          getOptionLabel={(o) => o.name}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={selected.length === 0 ? `Add ${compareMode === 'species' ? 'variants' : 'commodities'} to compare` : ''}
                            />
                          )}
                          renderOption={(props, option) => {
                            const color = overlays.includes(option.id)
                              ? overlayColorFor(option.id)
                              : OVERLAY_RAMP[(overlays.length) % OVERLAY_RAMP.length];
                            return (
                              <li {...props} key={option.id}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mr: 1.2, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: palette.text }}>{option.name}</Typography>
                              </li>
                            );
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const color = overlayColorFor(option.id);
                              const tagProps = getTagProps({ index });
                              return (
                                <Chip
                                  size="small"
                                  label={option.name}
                                  {...tagProps}
                                  key={option.id}
                                  sx={{
                                    bgcolor: `${color}22`,
                                    color: palette.text,
                                    border: `1px solid ${color}66`,
                                    '& .MuiChip-deleteIcon': { color: palette.textMuted, '&:hover': { color: palette.text } },
                                  }}
                                />
                              );
                            })
                          }
                        />
                      );
                    })()}
                  </Box>

                  {/* Context card - real market coverage (local) or price stats (global) */}
                  <Box sx={{ ...cardSx, p: 2.5 }}>
                    {isLocal ? (() => {
                      const cov = coverage[selectedCommodity];
                      return (
                        <>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text, mb: 2, letterSpacing: '-0.01em' }}>
                            Market Coverage
                          </Typography>
                          {cov ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              {[
                                { label: 'Markets reporting', node: <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1rem', color: palette.text }}>{cov.markets}</Typography> },
                                { label: 'Price records', node: <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1rem', color: palette.text }}>{cov.records.toLocaleString()}</Typography> },
                                { label: 'Range (per bag)', node: <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '0.95rem', color: palette.text }}>₦{Math.round(cov.priceMin).toLocaleString()} – ₦{Math.round(cov.priceMax).toLocaleString()}</Typography> },
                                { label: 'Average per kg', node: <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1rem', color: palette.accent }}>₦{Math.round(cov.pricePerKg).toLocaleString()}/kg</Typography> },
                                { label: 'Latest upload', node: <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '0.95rem', color: palette.text }}>{fmtDate(cov.latestDate)}</Typography> },
                              ].map((row, i, arr) => (
                                <Box key={row.label} sx={{ py: 1.25, borderBottom: i < arr.length - 1 ? `1px solid ${palette.divider}` : 'none' }}>
                                  <Typography sx={{ ...statLabelSx, mb: 0.6 }}>{row.label}</Typography>
                                  {row.node}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '0.82rem', color: palette.textMuted }}>No local upload data for this commodity yet.</Typography>
                          )}
                        </>
                      );
                    })() : (
                      <>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text, mb: 2, letterSpacing: '-0.01em' }}>
                          {worldView ? WORLD_COPY.statsHeading : 'Price Statistics'}
                        </Typography>
                        {hasSeries ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {[
                              { label: 'Current', node: statFigure(stats.current) },
                              { label: 'Change % (latest)', node: stats.changePercent == null ? statFigure(null) : <DeltaChip value={stats.changePercent} decimals={2} /> },
                              { label: worldView ? WORLD_COPY.highLabel : 'Period High', node: statFigure(stats.high) },
                              { label: worldView ? WORLD_COPY.lowLabel : 'Period Low', node: statFigure(stats.low) },
                            ].map((row, i, arr) => (
                              <Box key={row.label} sx={{ py: 1.25, borderBottom: i < arr.length - 1 ? `1px solid ${palette.divider}` : 'none' }}>
                                <Typography sx={{ ...statLabelSx, mb: 0.6 }}>{row.label}</Typography>
                                {row.node}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Icon icon="ph:plugs" width={18} height={18} color={palette.textMuted} />
                            <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, lineHeight: 1.55 }}>
                              {usingWorldReference
                                ? 'Statistics appear once the world reference prices load.'
                                : 'Statistics appear once the live exchange feed is connected.'}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
            </Grid>
          </Grid>
          </MotionInView>
        </Container>
      </Box>

      {/* ================================================================ */}
      {/* 03 / NIGERIAN MARKETS                                             */}
      {/* ================================================================ */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Nigerian Markets"
            number="03"
            title="Nigerian Commodity Prices"
            subtitle="Average market price per kilogram for each commodity variant, aggregated from local markets. Filter by category or search by name."
            sx={{ mb: { xs: 3, md: 3.5 } }}
          />

          {/* Category filter chips (multi-select) + search */}
          <MotionInView variants={fadeUp}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 3.5 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedCategories.length === 0}
                  onClick={() => setSelectedCategories([])}
                  onKeyDown={onKeyActivate(() => setSelectedCategories([]))}
                  sx={catChipSx(selectedCategories.length === 0)}
                >
                  All
                </Box>
                {categories.filter((cat) => cat.variantCount > 0).map((cat) => {
                  const active = selectedCategories.includes(cat.id);
                  const label = cat.name.split(' - ')[1] || cat.name;
                  return (
                    <Box
                      key={cat.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={active}
                      onClick={() => toggleCategory(cat.id)}
                      onKeyDown={onKeyActivate(() => toggleCategory(cat.id))}
                      sx={catChipSx(active)}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
              <TextField
                size="small"
                value={commoditySearch}
                onChange={(e) => setCommoditySearch(e.target.value)}
                placeholder="Search commodities…"
                inputProps={{ 'aria-label': 'Search commodities' }}
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="ph:magnifying-glass" width={17} height={17} color={palette.textMuted} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </MotionInView>

          {/* Commodity table */}
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, overflow: 'hidden' }}>
              {/* Table header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '2fr 1fr', md: '2.4fr 1fr 1fr 1fr' },
                  gap: 0,
                  px: 2.5,
                  py: 1.5,
                  bgcolor: palette.surface,
                  borderBottom: `1px solid ${palette.divider}`,
                }}
              >
                {['Commodity', 'Category', 'Price / kg', 'Updated'].map((h, idx) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: palette.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      textAlign: idx >= 2 ? 'right' : 'left',
                      display: { xs: idx === 1 || idx === 3 ? 'none' : 'block', md: 'block' },
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>

              {/* Table rows */}
              {filteredLocalCommodities.length === 0 ? (
                <Box sx={{ p: 5, textAlign: 'center' }}>
                  <Box sx={{ display: 'inline-flex', mb: 1.5, width: 48, height: 48, borderRadius: '50%', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, alignItems: 'center', justifyContent: 'center' }}>
                    <Icon icon="ph:magnifying-glass" width={22} height={22} color={palette.textMuted} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text, mb: 0.5 }}>No commodities match your filters</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: palette.textMuted }}>Try a different category or clear the search.</Typography>
                </Box>
              ) : (
                pagedCommodities.map((commodity, i) => {
                  const category = commodityCategories.find((c) => c.id === commodity.category);
                  const cov = coverage[commodity.id];
                  const perKg = cov?.pricePerKg != null ? Math.round(cov.pricePerKg) : null;
                  return (
                    <Box
                      key={commodity.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '2fr 1fr', md: '2.4fr 1fr 1fr 1fr' },
                        gap: 0,
                        px: 2.5,
                        py: 1.5,
                        borderBottom: i < pagedCommodities.length - 1 ? `1px solid ${palette.divider}` : 'none',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: palette.cardHover },
                        alignItems: 'center',
                      }}
                    >
                      {/* Name (with icon tile) */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon icon={iconForCommodity(commodity)} width={17} height={17} color={palette.navy} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.88rem', color: palette.text, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                            {commodity.name}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: '0.7rem', color: palette.textMuted }}>
                            {commodity.canonicalName}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Category code chip */}
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Chip
                          label={category?.code || '-'}
                          size="small"
                          sx={{
                            fontSize: '0.63rem', height: 20, fontWeight: 700, fontFamily: MONO,
                            bgcolor: palette.surface, color: palette.textSecondary, border: `1px solid ${palette.cardBorder}`, borderRadius: '4px',
                          }}
                        />
                      </Box>

                      {/* Price per kg (mono, right-aligned) - real aggregate from local uploads */}
                      <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '0.85rem', color: perKg != null ? palette.text : palette.textMuted, textAlign: 'right' }}>
                        {perKg != null ? (
                          <>
                            {'₦'}{perKg.toLocaleString()}
                            <Box component="span" sx={{ fontSize: '0.68rem', color: palette.textMuted, fontWeight: 500 }}>/kg</Box>
                          </>
                        ) : '-'}
                      </Typography>

                      {/* Updated (mono, right-aligned) - latest upload date from coverage */}
                      <Typography sx={{ ...numericSx, fontSize: '0.72rem', color: palette.textMuted, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                        {cov?.latestDate ? fmtDate(cov.latestDate) : '-'}
                      </Typography>
                    </Box>
                  );
                })
              )}

              {/* Footer - match count + pagination */}
              {filteredLocalCommodities.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2.5, py: 1, bgcolor: palette.surface, borderTop: `1px solid ${palette.divider}` }}>
                  <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary }}>
                    <Box component="span" sx={{ ...numericSx, fontWeight: 700, color: palette.text }}>{filteredLocalCommodities.length.toLocaleString()}</Box>
                    {` commodit${filteredLocalCommodities.length === 1 ? 'y' : 'ies'}`}
                  </Typography>
                  <TablePagination
                    component="div"
                    count={filteredLocalCommodities.length}
                    page={commSafePage}
                    onPageChange={(e, p) => setCommPage(p)}
                    rowsPerPage={commRowsPerPage}
                    onRowsPerPageChange={(e) => { setCommRowsPerPage(parseInt(e.target.value, 10)); setCommPage(0); }}
                    rowsPerPageOptions={[12, 24, 36]}
                    sx={paginationSx}
                  />
                </Box>
              )}
            </Box>
          </MotionInView>
        </Container>
      </Box>

      {/* ================================================================ */}
      {/* BOTTOM CTA                                                        */}
      {/* ================================================================ */}
      <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, position: 'relative', overflow: 'hidden', borderRadius: `${radius.panel}px`, p: { xs: 4.5, md: 7 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 4 }}>
              <Box aria-hidden sx={{ position: 'absolute', right: -80, bottom: -120, width: 520, height: 360, pointerEvents: 'none', background: 'radial-gradient(closest-side, rgba(34,197,94,0.09), transparent)' }} />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h2" sx={{ color: palette.text, fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 1.25 }}>Need deeper insights?</Typography>
                <Typography sx={{ color: palette.textSecondary, fontSize: '1rem', maxWidth: 470, lineHeight: 1.6 }}>
                  Access advanced analytics, historical trends, and market forecasts with the full dashboard.
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', gap: 1.75, flexShrink: 0 }}>
                <Button component={RouterLink} to="/login" variant="contained" disableElevation endIcon={<ArrowRight color="#fff" />}
                  sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, px: 3.25, py: 1.35, fontSize: '0.95rem', borderRadius: '10px', '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd } }}>
                  Access Full Analytics
                </Button>
              </Box>
            </Box>
          </MotionInView>
        </Container>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function PublicCommodities() {
  const c = useCommodities();
  const cov = useCommodityCoverage();
  const cat = useCategories();
  const gc = useGlobalCommodities();
  const gp = useGlobalPrices('ALL');
  const fx = useFxSeries('ALL');
  const sp = useGlobalSpecies();
  const spp = useSpeciesPrices('ALL');
  const lcs = useLocalCommoditySeries('ALL');
  const loading = [c, cov, cat, gc, gp, fx, sp, spp, lcs].some((q) => q.isLoading);
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <PublicHero
          eyebrow="Market Intelligence"
          title="Global & Local Commodities"
          subtitle="Loading real-time pricing across global futures and Nigerian markets…"
        />
        <LoadingSkeleton variant="page" contained />
      </Box>
    );
  }
  return (
    <PublicCommoditiesInner
      commodities={c.data || []}
      commodityCategories={cat.data || []}
      globalCommodities={gc.data || []}
      globalCommodityPrices={gp.data || {}}
      coverage={cov.data || {}}
      fxRates={fx.data || []}
      globalCommoditySpecies={sp.data || {}}
      globalSpeciesPrices={spp.data || {}}
      localCommodityPrices={lcs.data || {}}
    />
  );
}
